import React, { useEffect, useRef, useState } from 'react';
import {
  Collapsible,
  SidebarNavItem,
  Tonality,
} from '@dsny/dsny-component-library';
import { useStatsigUser } from '@statsig/react-bindings';
import {
  GroupsUnfilled,
  Keyboardarrowright,
} from '@dsny/dsny-component-library/icons';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/app/store';
import { syncAuthenticatedUserSession } from 'src/features/Auth/Auth.utils';
import { setMenuState, setSelectedCompany } from 'src/features/Home';
import {
  getMultiCompanyAccess,
  MultiCompanyAccessCompany,
  selectMultiCompanyAccessCompany,
  useHasMultiCompanyAccess,
} from 'src/features/MultiCompanyAccess';
import { PurchaseLeaveModal } from 'src/features/Purchase/PurchaseLeaveModal';
import { trackEvent } from 'src/utils/segmentService';
import {
  CustomNavItemStyles,
  CustomSidebarStyles,
  GradientOverlay,
  NavItemWrapper,
  SidebarSectionDivider,
  CompanyIconWrapper,
  CompanyInitials,
  CompanyInfoWrapper,
  CompanyLabel,
  CompanyArrowWrapper,
  CompanyTooltipStyles,
  CompanyNavLink,
  CompanySelectionPanelWrapper,
  SidebarWrapper,
} from './Sidebar.styles';
import CompanySelection from './CompanySelection/CompanySelection';
import CompanySelectionMobileModal from './CompanySelection/CompanySelectionMobileModal';
import SidebarFooter from './SidebarFooter';
import SidebarLogo from './SidebarLogo';

// Mobile width
const mobileWidthSize = 480;

// Get the initials of the company name
const getCompanyInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return '';
  }

  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }

  return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { updateUserAsync } = useStatsigUser();
  const { isSideMenuExpanded, selectedCompany } = useSelector(
    (state: RootState) => state.home
  );
  const { companyContext } = useSelector((state: RootState) => state.auth);
  const { companiesResponse, isFetchingCompanies, isSelectingCompany } =
    useSelector((state: RootState) => state.multiCompanyAccess);
  const hasMultiCompanyAccess = useHasMultiCompanyAccess();
  const [showModal, setShowModal] = useState(false);
  const [modalNavigateTo, setModalNavigateTo] = useState('');
  const [pendingCompanySelection, setPendingCompanySelection] =
    useState<MultiCompanyAccessCompany | null>(null);
  const [leaveModalAction, setLeaveModalAction] = useState<
    'navigation' | 'company-switch' | null
  >(null);
  const [isCompanySelectionOpen, setIsCompanySelectionOpen] = useState(false);
  const [isCompanySelectionMobileOpen, setIsCompanySelectionMobileOpen] =
    useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const [isCompanyLabelTruncated, setIsCompanyLabelTruncated] = useState(false);
  const sidebarWrapperRef = useRef<HTMLDivElement | null>(null);
  const companyTriggerRef = useRef<HTMLAnchorElement | null>(null);
  const companySelectionPanelRef = useRef<HTMLDivElement | null>(null);
  const companyLabelRef = useRef<HTMLSpanElement | null>(null);
  const companies = companiesResponse?.data.companies ?? [];
  const selectedCompanyData = companies.find(
    (company) => company.company_id === companyContext.companyId
  );
  const selectedCompanyName =
    selectedCompanyData?.business_name ?? selectedCompany;
  const companyInitials = getCompanyInitials(selectedCompanyName);

  // Update the truncation state of the company label
  const updateCompanyLabelTruncation = () => {
    if (!companyLabelRef.current) {
      setIsCompanyLabelTruncated(false);
      return;
    }

    setIsCompanyLabelTruncated(
      companyLabelRef.current.scrollWidth > companyLabelRef.current.clientWidth
    );
  };

  // Toggle the sidebar menu
  const toggleMenu = () => {
    setIsCompanySelectionOpen(false);
    setIsCompanySelectionMobileOpen(false);
    dispatch(setMenuState(!isSideMenuExpanded));
  };

  useEffect(() => {
    const isMobile = window.innerWidth <= mobileWidthSize;
    if (isMobile) {
      dispatch(setMenuState(false));
    }
  }, []);

  useEffect(() => {
    if (hasMultiCompanyAccess && !companiesResponse && !isFetchingCompanies) {
      dispatch(getMultiCompanyAccess());
    }
  }, [companiesResponse, dispatch, hasMultiCompanyAccess, isFetchingCompanies]);

  useEffect(() => {
    if (
      selectedCompanyData &&
      selectedCompany !== selectedCompanyData.business_name
    ) {
      dispatch(setSelectedCompany(selectedCompanyData.business_name));
    }
  }, [dispatch, selectedCompany, selectedCompanyData]);

  useEffect(() => {
    const handleViewportSwitch = () => {
      const isMobile = window.innerWidth <= mobileWidthSize;

      if (isMobile) {
        setIsCompanySelectionOpen(false);
      } else {
        setIsCompanySelectionMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleViewportSwitch);

    return () => {
      window.removeEventListener('resize', handleViewportSwitch);
    };
  }, []);

  useEffect(() => {
    updateCompanyLabelTruncation();
  }, [selectedCompanyName, isSideMenuExpanded]);

  // Handling sidebar navigation
  const handleNavigation = (navigateTo: string, blockFrom: string) => {
    setIsCompanySelectionOpen(false);
    setIsCompanySelectionMobileOpen(false);
    setPendingCompanySelection(null);
    setLeaveModalAction(null);
    const showModalBeforeNavigating = location.pathname.includes(blockFrom);
    const isMobile = window.innerWidth <= mobileWidthSize;

    if (isMobile) {
      dispatch(setMenuState(false));
    }

    if (showModalBeforeNavigating) {
      setLeaveModalAction('navigation');
      setShowModal(true);
      setModalNavigateTo(navigateTo);
    } else {
      setTimeout(() => {
        navigate(navigateTo);
      });
    }
  };

  // Handle closing the leave modal
  const handleCloseLeaveModal = (value: boolean): void => {
    if (!value) {
      setPendingCompanySelection(null);
      setLeaveModalAction(null);
    }
    setShowModal(value);
  };

  const performCompanySelection = (
    company: MultiCompanyAccessCompany
  ): void => {
    dispatch(selectMultiCompanyAccessCompany({ companyId: company.company_id }))
      .unwrap()
      .then(async () => {
        await syncAuthenticatedUserSession({
          updateUserAsync,
        });
        trackEvent('Company Selected', {
          source: 'in_app',
          selected_company_id: company.company_id.toString(),
          previous_company_id: String(companyContext.companyId ?? ''),
          company_count: companies.length.toString(),
        });
        setIsCompanySelectionOpen(false);
        setIsCompanySelectionMobileOpen(false);
        window.location.reload();
      })
      .catch(() => {
        // Keep the selection UI open when the company switch fails.
      });
  };

  // Update the position of the company selection panel
  const updateCompanySelectionPanelPosition = () => {
    if (!companyTriggerRef.current || !sidebarWrapperRef.current) {
      return;
    }

    const triggerRect = companyTriggerRef.current.getBoundingClientRect();
    const sidebarRect = sidebarWrapperRef.current.getBoundingClientRect();
    setPanelPosition({
      top: triggerRect.top - 12,
      left: sidebarRect.right,
    });
  };

  // Toggle the company selection panel
  const toggleCompanySelection = (
    event: React.MouseEvent<HTMLAnchorElement>
  ): void => {
    event.preventDefault();

    if (window.innerWidth <= mobileWidthSize) {
      setIsCompanySelectionOpen(false);
      setIsCompanySelectionMobileOpen(true);
      dispatch(setMenuState(false));
      return;
    }

    updateCompanySelectionPanelPosition();
    setIsCompanySelectionOpen((prev) => !prev);
  };

  // Handle company selection
  const handleSelectCompany = (company: MultiCompanyAccessCompany): void => {
    if (isSelectingCompany || company.company_id === companyContext.companyId) {
      setIsCompanySelectionOpen(false);
      setIsCompanySelectionMobileOpen(false);
      return;
    }

    const isSummaryStep =
      location.pathname === '/payment/summary' ||
      location.pathname === '/payment/enterprise/summary';

    if (isSummaryStep) {
      setPendingCompanySelection(company);
      setLeaveModalAction('company-switch');
      setShowModal(true);
      setIsCompanySelectionOpen(false);
      setIsCompanySelectionMobileOpen(false);
      return;
    }

    performCompanySelection(company);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideTrigger =
        !!companyTriggerRef.current?.contains(targetNode);
      const clickedInsidePanel =
        !!companySelectionPanelRef.current?.contains(targetNode);

      if (!clickedInsideTrigger && !clickedInsidePanel) {
        setIsCompanySelectionOpen(false);
      }
    };

    // Handle window resize
    const handleResize = () => {
      updateCompanySelectionPanelPosition();
    };

    if (isCompanySelectionOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('resize', handleResize);
    };
  }, [isCompanySelectionOpen]);

  return (
    <>
      <SidebarWrapper
        ref={sidebarWrapperRef}
        className={isSideMenuExpanded ? 'expanded' : ''}
      >
        <Collapsible
          width={240}
          isExpanded={isSideMenuExpanded}
          style={{ ...CustomSidebarStyles }}
        >
          <div>
            <SidebarLogo
              to="/home"
              isExpanded={isSideMenuExpanded}
              onClick={() => handleNavigation(`/home`, '/payment')}
            />

            {/* Multi-company selection */}
            {hasMultiCompanyAccess && (
              <>
                {isSideMenuExpanded ? (
                  <>
                    {!isCompanySelectionOpen && isCompanyLabelTruncated ? (
                      <Tonality.Tooltip
                        content={selectedCompanyName}
                        placement="right"
                        mode="light"
                        hasArrow={false}
                        containerStyles={{ ...CompanyTooltipStyles }}
                      >
                        <CompanyNavLink
                          ref={companyTriggerRef}
                          $isExpanded={isSideMenuExpanded}
                          to="/library"
                          onClick={toggleCompanySelection}
                          onMouseEnter={updateCompanyLabelTruncation}
                        >
                          <CompanyIconWrapper>
                            <GroupsUnfilled
                              width={24}
                              height={24}
                              display="flex"
                            />
                          </CompanyIconWrapper>
                          <CompanyInfoWrapper>
                            <CompanyLabel ref={companyLabelRef}>
                              {selectedCompanyName}
                            </CompanyLabel>
                            <CompanyArrowWrapper>
                              <Keyboardarrowright />
                            </CompanyArrowWrapper>
                          </CompanyInfoWrapper>
                        </CompanyNavLink>
                      </Tonality.Tooltip>
                    ) : (
                      <CompanyNavLink
                        ref={companyTriggerRef}
                        $isExpanded={isSideMenuExpanded}
                        to="/library"
                        onClick={toggleCompanySelection}
                        onMouseEnter={updateCompanyLabelTruncation}
                      >
                        <CompanyIconWrapper>
                          <GroupsUnfilled
                            width={24}
                            height={24}
                            display="flex"
                          />
                        </CompanyIconWrapper>
                        <CompanyInfoWrapper>
                          <CompanyLabel ref={companyLabelRef}>
                            {selectedCompanyName}
                          </CompanyLabel>
                          <CompanyArrowWrapper>
                            <Keyboardarrowright />
                          </CompanyArrowWrapper>
                        </CompanyInfoWrapper>
                      </CompanyNavLink>
                    )}
                  </>
                ) : (
                  <>
                    {!isCompanySelectionOpen ? (
                      <Tonality.Tooltip
                        content={selectedCompanyName}
                        placement="right"
                        mode="light"
                        hasArrow={false}
                        containerStyles={{ ...CompanyTooltipStyles }}
                      >
                        <CompanyNavLink
                          ref={companyTriggerRef}
                          $isExpanded={isSideMenuExpanded}
                          to="/library"
                          onClick={toggleCompanySelection}
                        >
                          <CompanyIconWrapper>
                            <CompanyInitials>{companyInitials}</CompanyInitials>
                          </CompanyIconWrapper>
                        </CompanyNavLink>
                      </Tonality.Tooltip>
                    ) : (
                      <CompanyNavLink
                        ref={companyTriggerRef}
                        $isExpanded={isSideMenuExpanded}
                        to="/library"
                        onClick={toggleCompanySelection}
                      >
                        <CompanyIconWrapper>
                          <CompanyInitials>{companyInitials}</CompanyInitials>
                        </CompanyIconWrapper>
                      </CompanyNavLink>
                    )}
                  </>
                )}

                <SidebarSectionDivider />
              </>
            )}

            {/* Dashboard */}
            <NavItemWrapper>
              <SidebarNavItem
                as={NavLink}
                url=""
                tabIndex={0}
                iconName="Home"
                label="Dashboard"
                isExpanded={isSideMenuExpanded}
                isActive={location.pathname === '/library'}
                isDisabled={false}
                isBackgroundTransparent={false}
                setting="mtr"
                mode="desktop"
                style={{ ...CustomNavItemStyles }}
                onClick={() => handleNavigation(`/library`, '/payment')}
              />
            </NavItemWrapper>

            {/* Airplay report */}
            <NavItemWrapper>
              <SidebarNavItem
                as={NavLink}
                url=""
                tabIndex={0}
                iconName="BarChart"
                label="Airplay report"
                isExpanded={isSideMenuExpanded}
                isActive={location.pathname === '/dashboard'}
                isDisabled={false}
                isBackgroundTransparent={false}
                setting="mtr"
                mode="desktop"
                style={{ ...CustomNavItemStyles }}
                onClick={() => handleNavigation('/dashboard', '/payment')}
              />
            </NavItemWrapper>
          </div>

          <SidebarFooter
            isExpanded={isSideMenuExpanded}
            onClick={() => toggleMenu()}
          />
        </Collapsible>

        {hasMultiCompanyAccess && isCompanySelectionOpen && (
          // Company selection panel
          <CompanySelectionPanelWrapper
            ref={companySelectionPanelRef}
            style={{
              top: `${panelPosition.top}px`,
              left: `${panelPosition.left}px`,
            }}
          >
            <CompanySelection
              companies={companies}
              selectedCompanyId={companyContext.companyId}
              onSelectCompany={handleSelectCompany}
            />
          </CompanySelectionPanelWrapper>
        )}
      </SidebarWrapper>

      {hasMultiCompanyAccess && (
        // Company selection modal for mobile
        <CompanySelectionMobileModal
          companies={companies}
          selectedCompanyId={companyContext.companyId}
          isOpen={isCompanySelectionMobileOpen}
          onClose={() => setIsCompanySelectionMobileOpen(false)}
          onSelectCompany={handleSelectCompany}
        />
      )}

      {/* Gradient Overlay */}
      <GradientOverlay
        className={isSideMenuExpanded ? 'active' : ''}
        onClick={() => toggleMenu()}
      />

      {/* Purchase leave modal */}
      <PurchaseLeaveModal
        show={showModal}
        setShowLeave={handleCloseLeaveModal}
        navigateTo={leaveModalAction === 'navigation' ? modalNavigateTo : ''}
        onConfirmLeave={
          leaveModalAction === 'company-switch' && pendingCompanySelection
            ? () => {
                const companyToSelect = pendingCompanySelection;
                setPendingCompanySelection(null);
                setLeaveModalAction(null);
                performCompanySelection(companyToSelect);
                navigate('/home');
              }
            : undefined
        }
      />
    </>
  );
};

export default Sidebar;
